import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Radio, Users, Eye, Gift, X, Square, Camera, CameraOff, Mic, MicOff, Loader2, ArrowLeft, Sparkles, Trophy, Send, Share2, Info, Swords, CheckCircle2, Music2, Gauge, Type, Smile, RefreshCw, Wand2, Volume2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Room, RoomEvent, Track, createLocalVideoTrack, createLocalAudioTrack } from "livekit-client";
import GiftSheet from "../components/GiftSheet";
import ShareSheet from "../components/ShareSheet";
import MusicPicker from "../components/MusicPicker";
import LiveInviteModal from "../components/LiveInviteModal";
import BattleOverlay from "../components/BattleOverlay";
import LiveEffectsCanvas from "../components/LiveEffectsCanvas";
import BattleSplitView from "../components/BattleSplitView";
import LiveChatFeed from "../components/LiveChatFeed";
import LiveViewersButton from "../components/LiveViewersButton";
import LiveGiftFlow from "../components/LiveGiftFlow";
import TopGiftersWidget from "../components/TopGiftersWidget";

// Phase 2 — Dramatic LUT-style filters (replaces the weak CSS presets)
const LIVE_FILTERS = [
  { id: "none",       name: "Original",       css: "none" },
  { id: "clarendon",  name: "Clarendon",      css: "contrast(1.20) saturate(1.35) brightness(1.05)" },
  { id: "gingham",    name: "Gingham",        css: "sepia(0.30) contrast(1.10) brightness(1.05) hue-rotate(-10deg)" },
  { id: "juno",       name: "Juno",           css: "contrast(1.15) saturate(1.60) brightness(1.03) hue-rotate(-8deg)" },
  { id: "lark",       name: "Lark",           css: "contrast(0.95) saturate(1.20) brightness(1.10) hue-rotate(10deg)" },
  { id: "reyes",      name: "Reyes",          css: "sepia(0.28) brightness(1.15) contrast(0.88) saturate(0.85)" },
  { id: "nashville",  name: "Nashville",      css: "sepia(0.30) contrast(1.35) brightness(1.06) saturate(1.40) hue-rotate(-15deg)" },
  { id: "toaster",    name: "Toaster",        css: "sepia(0.50) contrast(1.30) brightness(0.90) saturate(1.30) hue-rotate(-25deg)" },
  { id: "1977",       name: "1977",           css: "sepia(0.50) contrast(1.15) brightness(1.15) saturate(1.50) hue-rotate(-15deg)" },
  { id: "kodak",      name: "Kodak Portra",   css: "sepia(0.10) contrast(1.10) saturate(1.30) brightness(1.05) hue-rotate(-3deg)" },
  { id: "fuji",       name: "Fuji Superia",   css: "sepia(0.05) contrast(1.15) saturate(1.45) brightness(1.02) hue-rotate(3deg)" },
  { id: "teal_orange",name: "Teal-Orange",    css: "contrast(1.35) saturate(1.55) brightness(0.97) hue-rotate(-8deg)" },
  { id: "bollywood",  name: "Bollywood",      css: "sepia(0.15) contrast(1.20) saturate(1.75) brightness(1.05) hue-rotate(-10deg)" },
  { id: "neon",       name: "Neon Cyber",     css: "contrast(1.40) saturate(1.90) brightness(1.05) hue-rotate(180deg)" },
  { id: "vhs",        name: "VHS",            css: "contrast(1.25) saturate(1.30) brightness(1.05) hue-rotate(-6deg) sepia(0.10)" },
  { id: "mono",       name: "Mono Noir",      css: "grayscale(1) contrast(1.30) brightness(1.05)" },
  { id: "sunset",     name: "Sunset",         css: "sepia(0.20) contrast(1.15) brightness(1.10) saturate(1.55) hue-rotate(-18deg)" },
  { id: "rosegold",   name: "Rose Gold",      css: "sepia(0.18) contrast(1.05) brightness(1.10) saturate(1.30) hue-rotate(-12deg)" },
  { id: "moody",      name: "Moody Forest",   css: "contrast(1.20) saturate(0.85) brightness(0.90) hue-rotate(6deg)" },
  { id: "punjabi",    name: "Punjabi Wedding",css: "sepia(0.22) contrast(1.25) saturate(1.85) brightness(1.05) hue-rotate(-14deg)" },
  { id: "kbeauty",    name: "K-Beauty",       css: "brightness(1.15) contrast(0.95) saturate(1.10) sepia(0.07)" },
];

// Beauty sliders — turned into a live-updating CSS filter chain applied on top of the LUT filter.
// Each slider is 0-100.
const BEAUTY_DEFAULTS = { smooth: 0, bright: 0, whiten: 0, warm: 0, contrast: 0, vibrance: 0 };
function beautyToCss(b) {
  const parts = [];
  if (b.smooth > 0)   parts.push(`blur(${(b.smooth/100 * 1.4).toFixed(2)}px)`);
  if (b.bright > 0)   parts.push(`brightness(${(1 + b.bright/100 * 0.30).toFixed(2)})`);
  if (b.whiten > 0)   parts.push(`saturate(${(1 - b.whiten/100 * 0.20).toFixed(2)}) sepia(${(-b.whiten/100 * 0.05).toFixed(2)}) hue-rotate(${(b.whiten/100 * 4).toFixed(1)}deg)`);
  if (b.warm > 0)     parts.push(`sepia(${(b.warm/100 * 0.25).toFixed(2)}) hue-rotate(${(-b.warm/100 * 6).toFixed(1)}deg)`);
  if (b.contrast > 0) parts.push(`contrast(${(1 + b.contrast/100 * 0.30).toFixed(2)})`);
  if (b.vibrance > 0) parts.push(`saturate(${(1 + b.vibrance/100 * 0.60).toFixed(2)})`);
  return parts.join(" ").trim();
}
const BEAUTY_PRESETS = [
  { id: "off",         name: "Off",          b: { smooth:0, bright:0, whiten:0, warm:0, contrast:0, vibrance:0 } },
  { id: "natural",     name: "Natural",      b: { smooth:25, bright:12, whiten:8, warm:6, contrast:5, vibrance:15 } },
  { id: "soft_glow",   name: "Soft Glow",    b: { smooth:45, bright:22, whiten:14, warm:10, contrast:0, vibrance:20 } },
  { id: "kpop",        name: "K-Pop",        b: { smooth:60, bright:28, whiten:30, warm:0, contrast:5, vibrance:15 } },
  { id: "bollywood",   name: "Bollywood Diva",b:{ smooth:40, bright:20, whiten:5, warm:20, contrast:15, vibrance:40 } },
  { id: "fresh",       name: "Fresh",        b: { smooth:35, bright:25, whiten:12, warm:5, contrast:8, vibrance:25 } },
  { id: "barbie",      name: "Barbie",       b: { smooth:55, bright:30, whiten:20, warm:15, contrast:5, vibrance:35 } },
  { id: "bronze",      name: "Bronze",       b: { smooth:30, bright:8, whiten:0, warm:40, contrast:15, vibrance:30 } },
  { id: "rose",        name: "Rose",         b: { smooth:40, bright:20, whiten:10, warm:25, contrast:8, vibrance:20 } },
];

// Lens — real BG replacement backgrounds. `type` = "none" | "blur" | "image" | "color"
const LIVE_BACKGROUNDS = [
  { id: "none",    name: "Off",         type: "none",  value: null,  thumb: "#333" },
  { id: "blur",    name: "Blur",        type: "blur",  value: null,  thumb: "#666" },
  { id: "beach",   name: "Beach",       type: "image", value: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1080&q=70&fit=crop", thumb: "#FCD34D" },
  { id: "studio",  name: "Studio",      type: "image", value: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1080&q=70&fit=crop", thumb: "#000" },
  { id: "cafe",    name: "Cafe",        type: "image", value: "https://images.unsplash.com/photo-1445116572660-236099ec97a0?w=1080&q=70&fit=crop", thumb: "#A16207" },
  { id: "sunset",  name: "Sunset",      type: "image", value: "https://images.unsplash.com/photo-1495616811223-4d98c6e9c869?w=1080&q=70&fit=crop", thumb: "#FF6B35" },
  { id: "night",   name: "Neon Night",  type: "image", value: "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1080&q=70&fit=crop", thumb: "#4C1D95" },
  { id: "bedroom", name: "Bedroom",     type: "image", value: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1080&q=70&fit=crop", thumb: "#F472B6" },
  { id: "party",   name: "Party",       type: "image", value: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1080&q=70&fit=crop", thumb: "#EC4899" },
  { id: "office",  name: "Office",      type: "image", value: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1080&q=70&fit=crop", thumb: "#94A3B8" },
  { id: "library", name: "Library",     type: "image", value: "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1080&q=70&fit=crop", thumb: "#78350F" },
  { id: "temple",  name: "Temple",      type: "image", value: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=1080&q=70&fit=crop", thumb: "#DC2626" },
  { id: "concert", name: "Concert",     type: "image", value: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1080&q=70&fit=crop", thumb: "#7C3AED" },
  { id: "space",   name: "Space",       type: "image", value: "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1080&q=70&fit=crop", thumb: "#1E293B" },
  { id: "forest",  name: "Forest",      type: "image", value: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1080&q=70&fit=crop", thumb: "#059669" },
  { id: "city",    name: "City Skyline",type: "image", value: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1080&q=70&fit=crop", thumb: "#0EA5E9" },
  { id: "pink",    name: "Pink Wall",   type: "color", value: "#FFB6C1", thumb: "#FFB6C1" },
  { id: "black",   name: "Black",       type: "color", value: "#000000", thumb: "#111" },
  { id: "white",   name: "White",       type: "color", value: "#FFFFFF", thumb: "#EEE" },
  { id: "gold",    name: "Gold",        type: "color", value: "#FFD700", thumb: "#FFD700" },
];
const LIVE_STICKERS = ["❤️", "🔥", "✨", "🎉", "💯", "👀", "😂", "🌟", "💖", "🤩", "🎵", "🎬"];

function LiveStatsMini({ badge, level, tier }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="px-2 py-0.5 rounded-full bg-white/10 border border-white/20">Lv {level} • {tier}</span>
      <span className="px-2 py-0.5 rounded-full bg-[#FF7A00]/15 border border-[#FF7A00]/30 text-[#FF7A00] font-bold">{badge}</span>
    </div>
  );
}

function ActiveList({ onWatch }) {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => api.get("/live/active").then(r => { setStreams(r.data); setLoading(false); }).catch(()=>setLoading(false));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  if (loading) return <div className="text-center text-white/50 py-8">Loading...</div>;

  if (streams.length === 0) {
    return (
      <div className="text-center py-12 text-white/60">
        <Radio className="w-12 h-12 mx-auto mb-2 opacity-50"/>
        <p>No one is live right now</p>
        <p className="text-xs mt-1">Be the first to go live!</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3" data-testid="live-list">
      {streams.map(s => (
        <button key={s.id} onClick={()=>onWatch(s)} data-testid={`live-card-${s.id}`}
          className="relative aspect-[9/12] rounded-2xl overflow-hidden border border-white/10 active:scale-95 transition group bg-gradient-to-br from-[#FF2A5F]/30 via-[#A855F7]/20 to-[#00E5FF]/30">
          {s.mocked && s.mocked_url ? (
            <video src={s.mocked_url} muted autoPlay loop playsInline className="absolute inset-0 w-full h-full object-cover"/>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Radio className="w-10 h-10 text-white/40"/>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/85"/>
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-[#FF2A5F] text-[10px] font-extrabold uppercase tracking-wide">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/>LIVE
          </div>
          <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] flex items-center gap-1"><Eye className="w-3 h-3"/>{s.viewers}</div>
          <div className="absolute bottom-0 inset-x-0 p-2.5">
            <div className="flex items-center gap-1.5 mb-1">
              <img src={s.user?.avatar} className="w-6 h-6 rounded-full border border-white/30"/>
              <span className="text-xs font-bold truncate">@{s.user?.username}</span>
            </div>
            <p className="text-xs leading-tight line-clamp-2">{s.title}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

function GoLive({ onStarted }) {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("general");
  const [streaming, setStreaming] = useState(false);
  const [stream, setStream] = useState(null);
  const [liveKitConnected, setLiveKitConnected] = useState(false);
  const [err, setErr] = useState("");
  const [starting, setStarting] = useState(false); // true from first Go Live tap until broadcast started or failed
  const [stats, setStats] = useState(null);
  const [endResult, setEndResult] = useState(null);
  const [inviteKind, setInviteKind] = useState(null); // null | "invite" | "battle"
  const [livekitEnabled, setLivekitEnabled] = useState(false);
  const [musicOpen, setMusicOpen] = useState(false);
  const [music, setMusic] = useState(null);
  const [bgmPlaying, setBgmPlaying] = useState(false);
  // Live editor state
  const [editorPanel, setEditorPanel] = useState(null); // null | filter | beauty | lens | speed | text | sticker
  const [toolbarOpen, setToolbarOpen] = useState(false); // collapsed by default — tap wand icon to expand
  const [filterId, setFilterId] = useState("none");
  const [beautyPresetId, setBeautyPresetId] = useState("off");
  const [beauty, setBeauty] = useState(BEAUTY_DEFAULTS);
  const [bgId, setBgId] = useState("none");
  const [speed, setSpeed] = useState(1);
  const [overlays, setOverlays] = useState([]);
  const [textDraft, setTextDraft] = useState("");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [lensReady, setLensReady] = useState(false);
  const draggingRef = useRef(null);
  const bgmRef = useRef(null);
  const videoRef = useRef(null);
  const mediaRef = useRef(null);
  const roomRef = useRef(null);
  const videoTrackRef = useRef(null);

  const filterCss = (LIVE_FILTERS.find(f => f.id === filterId) || LIVE_FILTERS[0]).css;
  const beautyCss = beautyToCss(beauty);
  const combinedFilter = [filterCss === "none" ? "" : filterCss, beautyCss].filter(Boolean).join(" ").trim() || "none";
  const activeBg = LIVE_BACKGROUNDS.find(b => b.id === bgId) || LIVE_BACKGROUNDS[0];
  const useEffectsCanvas = activeBg.type !== "none";  // Only run MediaPipe when BG is being replaced

  // Reset lens-ready when background changes; force-hide overlay after 5s max even if MediaPipe fails
  useEffect(() => {
    if (!useEffectsCanvas) { setLensReady(false); return; }
    setLensReady(false);
    const t = setTimeout(() => setLensReady(true), 5000);
    return () => clearTimeout(t);
  }, [useEffectsCanvas, bgId]);

  // Apply CSS filter live to the local preview whenever it changes
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.style.filter = combinedFilter || "none";
  }, [combinedFilter, streaming]);

  // Re-attach the published LiveKit video track to the broadcaster preview
  // element AFTER it mounts (streaming flips to true after the track is
  // already published, so the attach inside connectLiveKitPublisher fires
  // when videoRef.current is still null — this effect runs once the
  // element is in the DOM).
  useEffect(() => {
    if (streaming && videoRef.current && videoTrackRef.current) {
      try {
        videoTrackRef.current.attach(videoRef.current);
      } catch (e) {
        console.warn("Publisher preview re-attach failed:", e);
      }
    }
  }, [streaming]);

  // Mic toggle — enable/disable audio track on the local MediaStream AND on the LiveKit publisher
  const toggleMic = async () => {
    const next = !micOn;
    setMicOn(next); // always update UI state
    // Local preview
    const m = mediaRef.current;
    (m?.getAudioTracks?.() || []).forEach(t => { t.enabled = next; });
    // Broadcast (LiveKit)
    try {
      const room = roomRef.current;
      if (room?.localParticipant?.setMicrophoneEnabled) {
        await room.localParticipant.setMicrophoneEnabled(next);
      }
    } catch { /* noop */ }
  };
  // Camera toggle — enable/disable video track on local + LiveKit publisher
  const toggleCam = async () => {
    const next = !camOn;
    setCamOn(next); // always update UI state
    const m = mediaRef.current;
    (m?.getVideoTracks?.() || []).forEach(t => { t.enabled = next; });
    try {
      const room = roomRef.current;
      if (room?.localParticipant?.setCameraEnabled) {
        await room.localParticipant.setCameraEnabled(next);
      }
    } catch { /* noop */ }
  };

  const addText = () => {
    if (!textDraft.trim()) return;
    setOverlays(o => [...o, { id: Math.random().toString(36).slice(2), type: "text", text: textDraft.slice(0, 60), x: 50, y: 50, color: "#FFD700", fontSize: 28 }]);
    setTextDraft("");
  };
  const addSticker = (s) => setOverlays(o => [...o, { id: Math.random().toString(36).slice(2), type: "sticker", text: s, x: 50, y: 50, fontSize: 40 }]);
  const removeOverlay = (id) => setOverlays(o => o.filter(x => x.id !== id));
  const onOverlayDrag = (id, dx, dy, rect) => {
    setOverlays(o => o.map(x => x.id === id ? {
      ...x,
      x: Math.max(0, Math.min(100, x.x + (dx / rect.width) * 100)),
      y: Math.max(0, Math.min(100, x.y + (dy / rect.height) * 100)),
    } : x));
  };

  useEffect(() => { api.get("/live/me/stats").then(r => setStats(r.data)); }, []);
  useEffect(() => { api.get("/integrations/status").then(r => setLivekitEnabled(!!r.data?.livekit)).catch(()=>{}); }, []);

  const startCameraLocal = async () => {
    try {
      const m = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
      mediaRef.current = m;
      if (videoRef.current) videoRef.current.srcObject = m;
      return true;
    } catch (e) {
      setErr("Camera permission denied or unavailable.");
      return false;
    }
  };

  const stopCameraLocal = () => {
    if (mediaRef.current) {
      mediaRef.current.getTracks().forEach(t => t.stop());
      mediaRef.current = null;
    }
  };

  const connectLiveKitPublisher = async (data) => {
    const room = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;
    await room.connect(data.livekit_url, data.livekit_token);

    // Explicit local video track creation with web-safe defaults.
    // Do NOT rely on setCameraEnabled(true) here — on some browsers it never
    // resolves and throws "Timeout starting video source". Instead build the
    // track ourselves with facingMode: 'user' and a modest resolution
    // (never 'environment' — that fails on desktop with no rear camera),
    // and retry once or twice with a short back-off.
    const buildVideoTrack = async () => {
      let lastErr;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          return await createLocalVideoTrack({
            facingMode: "user",
            resolution: { width: 640, height: 480, frameRate: 24 },
          });
        } catch (e) {
          lastErr = e;
          await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
        }
      }
      throw lastErr;
    };

    let videoTrack;
    try {
      videoTrack = await buildVideoTrack();
      videoTrackRef.current = videoTrack;
    } catch (camErr) {
      const raw = String(camErr?.message || camErr || "").toLowerCase();
      if (raw.includes("timeout") || raw.includes("starting video") || raw.includes("could not start")) {
        throw new Error("Camera could not start, please close other apps or tabs using your camera and try again");
      }
      if (raw.includes("permission") || raw.includes("notallowed")) {
        throw new Error("Camera permission denied. Enable camera access in your browser settings and try again");
      }
      if (raw.includes("notfound") || raw.includes("no device")) {
        throw new Error("No camera detected on this device. Please connect a camera and try again");
      }
      throw new Error("Camera could not start, please close other apps or tabs using your camera and try again");
    }

    // Audio is optional — never block the broadcast if the mic is denied.
    let audioTrack = null;
    try {
      audioTrack = await createLocalAudioTrack();
    } catch { /* mic optional */ }

    await room.localParticipant.publishTrack(videoTrack);
    if (audioTrack) {
      try { await room.localParticipant.publishTrack(audioTrack); } catch { /* noop */ }
    }

    // Attach for local preview (LiveEffectsCanvas also reads from videoRef).
    if (videoRef.current) {
      videoTrack.attach(videoRef.current);
    }
  };

  const disconnectLiveKit = async () => {
    if (roomRef.current) {
      try { await roomRef.current.disconnect(); } catch { /* noop */ }
      roomRef.current = null;
    }
  };

  const goLive = async (e) => {
    e.preventDefault();
    if (starting || streaming) return; // hard guard against double-submit
    setStarting(true);
    setErr(""); setEndResult(null);
    if (!title.trim()) { setErr("Title required"); setStarting(false); return; }
    let data;
    try {
      const resp = await api.post("/live/start", {
        title, category,
        music: music ? { id: music.id, title: music.title, artist: music.artist, url: music.url } : null,
      });
      data = resp.data;
    } catch (e) { setErr(formatErr(e)); setStarting(false); return; }

    // Phase 1: connect LiveKit publisher (real broadcast) BEFORE entering streaming UI.
    // If this fails, we abort — do NOT silently fall back to a local-only preview
    // (which shows a black or camera-only screen with no broadcast).
    if (data.livekit_token && data.livekit_url) {
      try {
        await connectLiveKitPublisher(data);
        setLiveKitConnected(true);
      } catch (lkErr) {
        setErr("LiveKit connect failed: " + (lkErr?.message || lkErr));
        // Roll back the backend-created stream so no ghost live session lingers.
        try { await api.post(`/live/${data.id}/end`); } catch { /* noop */ }
        try { await disconnectLiveKit(); } catch { /* noop */ }
        setStarting(false);
        return; // stay on the form so the broadcaster sees the error
      }
    } else {
      // No LiveKit configured (mocked/dev mode) — start local camera preview only.
      setLiveKitConnected(false);
      const ok = await startCameraLocal();
      if (!ok) { setStarting(false); return; }
    }

    // Phase 2: connection + camera publish confirmed — NOW enter streaming UI.
    setStream(data);
    setStreaming(true);
    setStarting(false);
    onStarted?.();

    // Optional BGM (non-blocking)
    if (music?.url) {
      try {
        const a = new Audio(music.url);
        a.loop = true; a.volume = 0.35; a.preload = "auto";
        a.crossOrigin = "anonymous";
        await a.play();
        bgmRef.current = a;
        setBgmPlaying(true);
      } catch (audioErr) {
        console.warn("BGM autoplay blocked or URL failed", audioErr);
        const a = new Audio(music.url);
        a.loop = true; a.volume = 0.35;
        bgmRef.current = a;
        setBgmPlaying(false);
      }
    }
  };

  const endLive = async () => {
    try {
      const { data } = await api.post(`/live/${stream.id}/end`);
      setEndResult(data);
      api.get("/live/me/stats").then(r => setStats(r.data));
    } catch { /* noop */ }
    if (bgmRef.current) { try { bgmRef.current.pause(); } catch (_err) { /* noop */ } bgmRef.current = null; }
    await disconnectLiveKit();
    stopCameraLocal();
    setStreaming(false);
    setStream(null);
    setLiveKitConnected(false);
    setTitle("");
    setMusic(null);
  };

  useEffect(() => () => { disconnectLiveKit(); stopCameraLocal(); if (bgmRef.current) { try { bgmRef.current.pause(); } catch (_err) { /* noop */ } } }, []);

  // Heartbeat — while genuinely streaming, ping the backend every 20s so the
  // server's stale-stream reaper knows this broadcast is truly alive.
  // Also fires on `visibilitychange` (when the page becomes visible again
  // after the OS suspended background timers on mobile) and on LiveKit's
  // `Reconnected` event (after a transient network drop). Warns to the
  // console if the ping fails or returns ok:false so we can trace mid-stream
  // heartbeat loss in real user sessions.
  useEffect(() => {
    if (!streaming || !stream?.id) return;
    const ping = async (reason) => {
      try {
        const r = await api.post(`/live/${stream.id}/heartbeat`);
        if (!r?.data?.ok) {
          console.warn(`[hifireels] heartbeat ok:false (reason=${reason}, stream=${stream.id})`, r?.data);
        }
      } catch (err) {
        console.warn(`[hifireels] heartbeat failed (reason=${reason}, stream=${stream.id})`, err?.message || err);
      }
    };
    const iv = setInterval(() => ping("interval"), 20000);
    ping("initial");

    const onVis = () => { if (document.visibilityState === "visible") ping("visibilitychange"); };
    document.addEventListener("visibilitychange", onVis);

    // Bind LiveKit Reconnected → heartbeat.
    const room = roomRef.current;
    const onReconnected = () => ping("livekit-reconnected");
    if (room?.on) {
      try { room.on(RoomEvent.Reconnected, onReconnected); } catch { /* noop */ }
    }

    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      if (room?.off) {
        try { room.off(RoomEvent.Reconnected, onReconnected); } catch { /* noop */ }
      }
    };
  }, [streaming, stream?.id]);

  // On tab close / hard navigation, best-effort end the stream so it never lingers.
  useEffect(() => {
    if (!streaming || !stream?.id) return;
    const bye = () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/live/${stream.id}/end`;
        const token = localStorage.getItem("hf_token") || "";
        const blob = new Blob([JSON.stringify({})], { type: "application/json" });
        // sendBeacon can't set headers, so we call POST via fetch with keepalive.
        fetch(url, { method: "POST", headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }, body: "{}", keepalive: true }).catch(() => {});
      } catch { /* noop */ }
    };
    window.addEventListener("beforeunload", bye);
    window.addEventListener("pagehide", bye);
    return () => {
      window.removeEventListener("beforeunload", bye);
      window.removeEventListener("pagehide", bye);
    };
  }, [streaming, stream?.id]);

  // ── PK Battle polling (broadcaster side) ─────────────────────────────
  // Polls /battles/mine/current so the broadcaster screen can show the
  // split view when their stream is in an active battle. Independent of
  // BattleOverlay (which handles the invite popup + banner / timer).
  const [myBattle, setMyBattle] = useState(null);
  const [hostChatTick, setHostChatTick] = useState(0);
  const [myBattleSide, setMyBattleSide] = useState(null);
  useEffect(() => {
    if (!streaming || !stream?.id) { setMyBattle(null); setMyBattleSide(null); return; }
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get("/battles/mine/current");
        if (!alive) return;
        const b = r.data;
        if (b && b.status === "active") {
          setMyBattle(b);
          setMyBattleSide(b.host_stream_id === stream.id ? "host" : "opponent");
        } else {
          setMyBattle(null);
          setMyBattleSide(null);
        }
      } catch { /* noop */ }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [streaming, stream?.id]);

  // Poll live gifts received so the broadcaster also sees the rising-gift
  // animation whenever a supporter sends them a gift.
  const [myLiveGifts, setMyLiveGifts] = useState([]);
  useEffect(() => {
    if (!streaming || !stream?.id) { setMyLiveGifts([]); return; }
    let alive = true;
    const tick = () => { api.get(`/live/${stream.id}/gifts`).then(r => { if (alive) setMyLiveGifts(r.data || []); }).catch(() => {}); };
    tick();
    const t = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [streaming, stream?.id]);

  if (streaming && stream) {
    return (
      <div className="fixed inset-0 z-[55] bg-black" data-testid="broadcaster-fullscreen">
        {/* Error banner — visible if anything goes wrong AFTER entering broadcaster mode
            (e.g. LiveKit disconnects mid-stream). Sits above all overlays. */}
        {err && (
          <div className="absolute top-0 inset-x-0 z-[60] bg-[#FF2A5F] text-white text-xs font-bold px-3 py-2 text-center shadow-lg" data-testid="broadcaster-error">
            {err}
          </div>
        )}
        {/* Full-screen camera preview.
            When background replacement is active, the LiveEffectsCanvas takes over.
            The raw <video> is still needed as the segmentation source but hidden.
            During an active PK battle both are shrunk to the LEFT half. */}
        <video ref={videoRef} autoPlay playsInline muted
          className={`${myBattle ? "absolute battle-half left-0 w-1/2 rounded-none border-r border-white/10" : "absolute inset-0 w-full h-full"} object-cover ${useEffectsCanvas ? "opacity-0 pointer-events-none" : ""}`}
          style={{ filter: useEffectsCanvas ? "none" : (combinedFilter || "none") }}
          data-testid="broadcaster-preview"/>

        {useEffectsCanvas && (
          <>
            <LiveEffectsCanvas
              sourceVideoRef={videoRef}
              background={activeBg}
              filterCss={combinedFilter}
              className={myBattle ? "absolute battle-half left-0 w-1/2 object-cover rounded-none border-r border-white/10" : "absolute inset-0 w-full h-full object-cover"}
              fps={24}
              onReady={() => setLensReady(true)}
            />
            {/* Warm-up skeleton — MediaPipe WASM takes 1-2s to init on first background pick.
                Dismisses the instant the segmenter emits its first frame (via onReady), or after 5s max fallback. */}
            {!lensReady && (
              <div className="absolute inset-0 z-[1] flex items-center justify-center pointer-events-none bg-black/40 backdrop-blur-sm animate-in fade-in duration-300"
                   data-testid="live-effects-warmup">
                <div className="flex items-center gap-2 text-white/90 text-xs font-bold">
                  <div className="w-4 h-4 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin"/>
                  Warming up lens...
                </div>
              </div>
            )}
          </>
        )}

        {/* Camera-off — show streamer's profile avatar (Moj-style) */}
        {!camOn && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gradient-to-br from-[#0B0F19] via-[#1A0F2E] to-[#0B0F19]" data-testid="live-cam-off-overlay">
            {user?.avatar && (
              <img src={user.avatar} alt={user.username || "host"}
                className="w-40 h-40 rounded-full object-cover border-4 border-[#FF2A5F] shadow-[0_0_60px_rgba(255,42,95,0.45)]"/>
            )}
            <div className="mt-4 text-lg font-outfit font-extrabold">@{user?.username || "creator"}</div>
            <div className="mt-1 text-white/60 text-xs">Camera is off · Still live 🎤</div>
          </div>
        )}

        {/* Draggable overlays */}
        {overlays.map(o => (
          <div
            key={o.id}
            data-testid={`live-overlay-${o.id}`}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              draggingRef.current = { id: o.id, lastX: e.clientX, lastY: e.clientY };
            }}
            onPointerMove={(e) => {
              const drag = draggingRef.current;
              if (!drag || drag.id !== o.id) return;
              const rect = e.currentTarget.parentElement.getBoundingClientRect();
              const dx = e.clientX - drag.lastX, dy = e.clientY - drag.lastY;
              draggingRef.current = { id: o.id, lastX: e.clientX, lastY: e.clientY };
              onOverlayDrag(o.id, dx, dy, rect);
            }}
            onPointerUp={() => { draggingRef.current = null; }}
            className="absolute select-none cursor-move font-extrabold drop-shadow-lg"
            style={{
              left: `${o.x}%`, top: `${o.y}%`,
              transform: "translate(-50%, -50%)",
              color: o.color, fontSize: o.fontSize, lineHeight: 1,
              textShadow: o.type === "text" ? "0 2px 12px rgba(0,0,0,0.85)" : "none",
              touchAction: "none", zIndex: 25,
            }}
          >
            {o.text}
            <button onClick={() => removeOverlay(o.id)} className="absolute -top-2 -right-3 w-5 h-5 rounded-full bg-[#FF2A5F] text-white text-[11px] flex items-center justify-center">×</button>
          </div>
        ))}

        {/* Top HUD — LIVE badge + viewers + monitor notice */}
        <div className="absolute top-3 inset-x-3 z-30 flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FF2A5F] font-extrabold text-xs">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse"/>LIVE
            </div>
            {music && (
              <button onClick={async () => {
                  const a = bgmRef.current; if (!a) return;
                  if (bgmPlaying) { a.pause(); setBgmPlaying(false); }
                  else { try { await a.play(); setBgmPlaying(true); } catch (e) { window.alert("Cannot start music: " + (e?.message || e)); } }
                }} data-testid="live-bgm-toggle"
                className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur text-xs flex items-center gap-1.5 border border-[#A855F7]/50 active:scale-95">
                <Music2 className="w-3.5 h-3.5 text-[#A855F7]"/>
                <span className="font-bold truncate max-w-[100px]">{bgmPlaying ? music.title : "▶ Music"}</span>
              </button>
            )}
          </div>
          <LiveViewersButton streamId={stream.id} fallback={stream.viewers || 0} className="!px-3 !py-1.5"/>
        </div>

        {/* Monitor banner (persistent) */}
        <div data-testid="live-monitor-banner" className="absolute top-14 inset-x-3 z-[35] rounded-xl bg-black/55 backdrop-blur-md border border-[#FF7A00]/40 px-3 py-1.5 text-[10px] leading-tight text-white text-center">
          <span className="text-[#FF7A00] font-bold">Welcome to Hifireels.</span> This chat/live is <b>monitored</b> — be respectful and follow our guidelines.
        </div>

        {/* Right-side vertical editor toolbar — collapsed behind a single
            toggle button per support fix #7. When collapsed only the toggle
            shows; when expanded, the full toolbar slides in. */}
        <button type="button" onClick={() => setToolbarOpen(o => !o)} data-testid="live-tools-toggle"
          className={`absolute right-2 top-24 z-40 w-10 h-10 rounded-full flex items-center justify-center shadow-xl active:scale-95 ${toolbarOpen ? "bg-[#FF7A00] text-black" : "bg-black/70 backdrop-blur border border-white/20 text-white"}`}>
          {toolbarOpen ? <X className="w-5 h-5"/> : <Wand2 className="w-5 h-5"/>}
        </button>
        {toolbarOpen && (
        <div className="absolute right-2 top-[144px] bottom-40 z-30 w-[68px] flex flex-col gap-2 overflow-y-auto scrollbar-hide" data-testid="live-right-toolbar">
          {/* Mic toggle */}
          <button type="button" onClick={toggleMic} data-testid="live-tool-mic"
            className={`flex flex-col items-center gap-0.5 py-2 rounded-xl text-[10px] font-bold active:scale-95 ${micOn ? "bg-emerald-500/25 border border-emerald-400/50 text-emerald-300" : "bg-[#FF2A5F]/25 border border-[#FF2A5F]/50 text-[#FF2A5F]"}`}>
            {micOn ? <Mic className="w-4 h-4"/> : <MicOff className="w-4 h-4"/>}
            {micOn ? "Mic On" : "Mic Off"}
          </button>
          {/* Camera toggle */}
          <button type="button" onClick={toggleCam} data-testid="live-tool-cam"
            className={`flex flex-col items-center gap-0.5 py-2 rounded-xl text-[10px] font-bold active:scale-95 ${camOn ? "bg-emerald-500/25 border border-emerald-400/50 text-emerald-300" : "bg-[#FF2A5F]/25 border border-[#FF2A5F]/50 text-[#FF2A5F]"}`}>
            {camOn ? <Camera className="w-4 h-4"/> : <CameraOff className="w-4 h-4"/>}
            {camOn ? "Cam On" : "Cam Off"}
          </button>
          {[
            { id: "filter",  icon: Sparkles, label: "Filter" },
            { id: "beauty",  icon: Wand2,    label: "Beauty" },
            { id: "lens",    icon: Smile,    label: "Lens" },
            { id: "speed",   icon: Gauge,    label: "Speed" },
            { id: "text",    icon: Type,     label: "Text" },
            { id: "music",   icon: Music2,   label: "Music" },
          ].map(t => {
            const Ic = t.icon;
            const active = editorPanel === t.id;
            return (
              <button key={t.id} type="button"
                onClick={() => {
                  if (t.id === "music") { setMusicOpen(true); return; }
                  setEditorPanel(p => p === t.id ? null : t.id);
                }}
                data-testid={`live-tool-${t.id}`}
                className={`flex flex-col items-center gap-0.5 py-2 rounded-xl text-[10px] font-bold active:scale-95 ${active ? "bg-[#A855F7] text-white" : "bg-black/55 backdrop-blur border border-white/15 text-white/85"}`}>
                <Ic className="w-4 h-4"/>{t.label}
              </button>
            );
          })}
          {/* Flip camera */}
          <button type="button" onClick={async () => {
              try {
                const track = mediaRef.current?.getVideoTracks?.()[0];
                if (!track) return;
                const cur = track.getSettings?.().facingMode;
                stopCameraLocal();
                const m = await navigator.mediaDevices.getUserMedia({ video: { facingMode: cur === "user" ? "environment" : "user" }, audio: true });
                mediaRef.current = m;
                if (videoRef.current) videoRef.current.srcObject = m;
              } catch (e) { /* noop */ }
            }} data-testid="live-tool-flip"
            className="flex flex-col items-center gap-0.5 py-2 rounded-xl text-[10px] font-bold bg-black/55 backdrop-blur border border-white/15 text-white/85 active:scale-95">
            <RefreshCw className="w-4 h-4"/>Flip
          </button>
        </div>
        )}

        {/* Editor panel body — floating above bottom controls */}
        {editorPanel && (
          <div className={`absolute left-3 right-[82px] bottom-28 z-30 rounded-2xl bg-black/80 backdrop-blur-md border border-white/10 p-3 overflow-y-auto ${editorPanel === "beauty" || editorPanel === "lens" ? "max-h-[300px]" : "max-h-[140px]"}`} data-testid="live-editor-panel">
            {editorPanel === "filter" && (
              <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="live-filters">
                {LIVE_FILTERS.map(f => (
                  <button key={f.id} type="button" onClick={() => setFilterId(f.id)}
                    data-testid={`live-filter-${f.id}`}
                    className={`flex-shrink-0 px-3 py-2 rounded-xl text-[10px] font-bold active:scale-95 border ${filterId === f.id ? "bg-[#A855F7] text-white border-[#A855F7]" : "bg-white/5 text-white/85 border-white/10"}`}>
                    {f.name}
                  </button>
                ))}
              </div>
            )}
            {editorPanel === "beauty" && (
              <div data-testid="live-beauty-panel" className="space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-white/60 font-bold">Beauty Presets</div>
                <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="live-beauty-presets">
                  {BEAUTY_PRESETS.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => { setBeautyPresetId(p.id); setBeauty(p.b); }}
                      data-testid={`live-beauty-preset-${p.id}`}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-[10px] font-bold active:scale-95 border ${beautyPresetId === p.id ? "bg-[#FF7A00] text-black border-[#FF7A00]" : "bg-white/5 text-white/85 border-white/10"}`}>
                      {p.name}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/60 font-bold pt-1">Custom Beauty (6 sliders)</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                  {[
                    { key: "smooth",   label: "Smooth Skin" },
                    { key: "bright",   label: "Brighten" },
                    { key: "whiten",   label: "Whiten" },
                    { key: "warm",     label: "Warm Tone" },
                    { key: "contrast", label: "Contrast" },
                    { key: "vibrance", label: "Vibrance" },
                  ].map(s => (
                    <div key={s.key}>
                      <div className="flex items-center justify-between text-[10px] text-white/70 font-semibold">
                        <span>{s.label}</span>
                        <span className="tabular-nums text-[#FF7A00]">{beauty[s.key]}</span>
                      </div>
                      <input
                        type="range" min={0} max={100} value={beauty[s.key]}
                        onChange={e => { setBeautyPresetId("custom"); setBeauty(b => ({ ...b, [s.key]: parseInt(e.target.value) })); }}
                        data-testid={`live-beauty-${s.key}`}
                        className="w-full accent-[#FF2A5F] h-1.5"/>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {editorPanel === "lens" && (
              <div data-testid="live-lens" className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-white/60 font-bold">Background Lens (person segmentation)</div>
                  {activeBg.type !== "none" && (
                    <span className="text-[9px] text-emerald-400 font-bold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/>Live
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-1.5" data-testid="live-lens-grid">
                  {LIVE_BACKGROUNDS.map(b => (
                    <button key={b.id} type="button" onClick={() => setBgId(b.id)}
                      data-testid={`live-bg-${b.id}`}
                      className={`relative aspect-square rounded-lg overflow-hidden active:scale-95 border-2 ${bgId === b.id ? "border-[#00E5FF]" : "border-white/10"}`}
                      style={{ background: b.type === "image" ? `center / cover no-repeat url(${b.value})` : b.thumb }}>
                      {b.type === "blur" && <div className="absolute inset-0 backdrop-blur-md bg-white/10"/>}
                      <div className="absolute bottom-0 inset-x-0 px-1 py-0.5 bg-black/60 text-[9px] font-bold text-center truncate">{b.name}</div>
                      {bgId === b.id && <div className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-[#00E5FF] text-black text-[9px] flex items-center justify-center font-black">✓</div>}
                    </button>
                  ))}
                </div>
                <p className="text-[9px] text-white/45 pt-1">✨ Powered by MediaPipe person segmentation. Runs 20-30 FPS on-device.</p>
              </div>
            )}
            {editorPanel === "speed" && (
              <div className="flex gap-1.5" data-testid="live-speeds">
                {[0.5, 1, 2].map(s => (
                  <button key={s} type="button" onClick={() => { setSpeed(s); if (videoRef.current) videoRef.current.playbackRate = s; }}
                    data-testid={`live-speed-${s}`}
                    className={`flex-1 py-2 rounded-xl text-xs font-bold active:scale-95 ${speed === s ? "bg-[#FF7A00] text-black" : "bg-white/8 text-white/85"}`}>
                    {s}x
                  </button>
                ))}
              </div>
            )}
            {editorPanel === "text" && (
              <div data-testid="live-text-editor" className="space-y-2">
                <div className="flex items-center gap-2">
                  <input value={textDraft} onChange={e => setTextDraft(e.target.value)} placeholder="Type text overlay…"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addText(); } }}
                    data-testid="live-text-input"
                    className="flex-1 px-3 py-2 rounded-xl bg-white/8 border border-white/10 outline-none text-sm"/>
                  <button type="button" onClick={addText} data-testid="live-text-add"
                    className="px-3 py-2 rounded-xl bg-[#FF7A00] text-black font-bold text-xs active:scale-95">+ Add</button>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="live-stickers">
                  {LIVE_STICKERS.map(s => (
                    <button key={s} type="button" onClick={() => addSticker(s)}
                      data-testid={`live-sticker-${s}`}
                      className="text-xl p-1 rounded-lg bg-white/5 hover:bg-white/15 active:scale-90">{s}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bottom controls: title bar + action buttons */}
        <div className="absolute bottom-0 inset-x-0 z-30 bg-gradient-to-t from-black via-black/85 to-transparent pt-6 px-3 pb-4 space-y-2">
          <div>
            <p className="font-bold text-sm truncate">{stream.title}</p>
            <p className="text-[10px] text-white/60 mt-0.5">Room: {stream.livekit_room} • {liveKitConnected ? <b className="text-emerald-400">LIVE WebRTC</b> : (stream.mocked ? <b className="text-[#FF7A00]">MOCKED LiveKit</b> : <b className="text-[#FF2A5F]">NOT BROADCASTING</b>)}</p>
          </div>
          {stream?.id && <HostChatBar streamId={stream.id} onSent={() => setHostChatTick(t => t + 1)}/>}
          <div className="flex gap-2">
            <button onClick={() => setInviteKind("battle")} data-testid="invite-battle" className="flex-1 py-3 rounded-full bg-[#FF7A00]/15 border border-[#FF7A00]/40 text-[#FF7A00] font-bold flex items-center justify-center gap-2 active:scale-95">
              <Swords className="w-4 h-4"/>Battle
            </button>
            <button onClick={() => setInviteKind("invite")} data-testid="invite-friends" className="flex-1 py-3 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] font-bold flex items-center justify-center gap-2 active:scale-95">
              <Share2 className="w-4 h-4"/>Invite
            </button>
            <button onClick={endLive} data-testid="end-live" className="flex-1 py-3 rounded-full bg-[#FF2A5F] text-white font-outfit font-bold neon-pink active:scale-95 flex items-center justify-center gap-2">
              <Square className="w-4 h-4 fill-white"/>End
            </button>
          </div>
        </div>

        {/* Viewer comments — host sees every comment on the inner screen */}
        {stream?.id && <LiveChatFeed streamId={stream.id} refreshKey={hostChatTick} isHost hostUserId={user?.id} className="absolute left-3 right-20 bottom-48 z-20 max-h-48"/>}

        {/* PK battle: incoming invite popup + battle banner/countdown (polls while live) */}
        <BattleOverlay me={user} />

        {/* PK battle split-screen — opens an isolated second LiveKit connection
            to the opponent's stream while in an active battle. Primary
            publisher connection is NEVER disconnected. */}
        <BattleSplitView battle={myBattle} mySide={myBattleSide}/>

        {/* Rising-gift animation on the broadcaster's own screen whenever
            a supporter sends them a live gift. */}
        <LiveGiftFlow gifts={myLiveGifts} battle={myBattle} mySide={myBattleSide}/>

        {/* Invite modal — overlays on top, does NOT unmount live stream */}
        {inviteKind && (
          <LiveInviteModal
            streamId={stream.id}
            streamTitle={stream.title}
            kind={inviteKind}
            onClose={() => setInviteKind(null)}
          />
        )}

        {/* Music picker modal — accessible during live */}
        <MusicPicker open={musicOpen} onClose={() => setMusicOpen(false)} onSelect={async (m) => {
          setMusic(m);
          setMusicOpen(false);
          // Start playing BGM
          try {
            if (bgmRef.current) { try { bgmRef.current.pause(); } catch { /* noop */ } }
            const a = new Audio(m.url);
            a.loop = true; a.volume = 0.35; a.preload = "auto"; a.crossOrigin = "anonymous";
            await a.play();
            bgmRef.current = a;
            setBgmPlaying(true);
          } catch (e) { /* autoplay blocked — user can tap the pill */ }
        }} selected={music?.id}/>
      </div>
    );
  }

  if (endResult) {
    return (
      <div className="text-center py-8" data-testid="end-result">
        <Trophy className="w-16 h-16 text-[#FF7A00] mx-auto mb-3"/>
        <h3 className="font-outfit font-extrabold text-2xl">Stream Ended!</h3>
        <p className="text-white/70 text-sm mt-1">Streamed for {endResult.minutes} minutes</p>
        <div className="mt-5 grid grid-cols-3 gap-2 max-w-xs mx-auto">
          <div className="p-3 rounded-2xl bg-white/5 border border-white/10">
            <div className="font-outfit font-extrabold text-xl text-[#00E5FF]">+{endResult.xp_gained}</div>
            <div className="text-[10px] text-white/60 uppercase">XP</div>
          </div>
          <div className="p-3 rounded-2xl bg-white/5 border border-white/10">
            <div className="font-outfit font-extrabold text-xl">Lv {endResult.new_level}</div>
            <div className="text-[10px] text-white/60 uppercase">{endResult.tier}</div>
          </div>
          <div className="p-3 rounded-2xl bg-white/5 border border-white/10">
            <div className="font-outfit font-extrabold text-xl text-[#FF7A00]">{endResult.new_badge}</div>
            <div className="text-[10px] text-white/60 uppercase">Badge</div>
          </div>
        </div>
        <button onClick={()=>setEndResult(null)} className="mt-5 px-6 py-2.5 rounded-full bg-[#00E5FF] text-black font-bold">Done</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {stats && (
        <div className="rounded-3xl p-4 bg-gradient-to-br from-[#FF2A5F]/15 to-[#00E5FF]/10 border border-white/10" data-testid="my-stats">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-outfit font-bold flex items-center gap-1"><Trophy className="w-4 h-4 text-[#FF7A00]"/>My Streamer Stats</h3>
            <LiveStatsMini badge={stats.badge} level={stats.level} tier={stats.tier}/>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div><div className="font-bold">{stats.streams}</div><div className="text-[9px] text-white/60 uppercase">Streams</div></div>
            <div><div className="font-bold">{stats.minutes}m</div><div className="text-[9px] text-white/60 uppercase">Time</div></div>
            <div><div className="font-bold">{stats.viewers}</div><div className="text-[9px] text-white/60 uppercase">Viewers</div></div>
            <div><div className="font-bold text-[#FF7A00]">{stats.xp}</div><div className="text-[9px] text-white/60 uppercase">XP</div></div>
          </div>
          <div className="mt-2">
            <div className="flex justify-between text-[10px] text-white/60 mb-1"><span>Lv {stats.level}</span><span>{stats.xp}/{stats.next_level_xp} XP</span></div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#00E5FF] to-[#FF2A5F]" style={{width: `${stats.progress_pct}%`}}/></div>
          </div>
        </div>
      )}

      {livekitEnabled ? (
        <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex gap-2 text-xs" data-testid="livekit-status-banner">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5"/>
          <p className="text-white/80">LiveKit Cloud is <b className="text-emerald-400">configured</b>. Real WebRTC broadcast will start once your camera is ready — you'll see "LIVE WebRTC" on-screen when viewers can see you.</p>
        </div>
      ) : (
        <div className="p-3 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/20 flex gap-2 text-xs">
          <Info className="w-4 h-4 text-[#FF7A00] flex-shrink-0 mt-0.5"/>
          <p className="text-white/80">Live streaming uses <b className="text-[#FF7A00]">MOCKED LiveKit</b>. Your camera will preview locally; viewers see a sample loop. Real WebRTC activates when LiveKit Cloud keys are added.</p>
        </div>
      )}

      <form onSubmit={goLive} className="space-y-3" data-testid="golive-form">
        <input data-testid="golive-title" required placeholder="Stream title" value={title} onChange={e=>setTitle(e.target.value)} className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF]"/>
        <select value={category} onChange={e=>{ setCategory(e.target.value); e.target.blur(); }} className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none" data-testid="golive-category">
          <option value="creator" className="bg-[#0B0F19]">🎨 Creator</option>
          <option value="vlogger" className="bg-[#0B0F19]">📹 Vlogger</option>
          <option value="artist" className="bg-[#0B0F19]">🎭 Artist</option>
          <option value="model" className="bg-[#0B0F19]">💃 Model</option>
          <option value="singer" className="bg-[#0B0F19]">🎤 Singer</option>
          <option value="private_room" className="bg-[#0B0F19]">🔒 Private Chat Room</option>
          <option value="couple_room" className="bg-[#0B0F19]">💑 Couple Chat Room</option>
          <option value="concert_room" className="bg-[#0B0F19]">🎤 Concert / DJ / Fashion</option>
        </select>

        {/* Background Music picker */}
        {music ? (
          <div className="p-3 rounded-2xl bg-[#A855F7]/15 border border-[#A855F7]/40 flex items-center gap-2" data-testid="golive-music-selected">
            <Music2 className="w-4 h-4 text-[#A855F7]"/>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{music.title}</div>
              <div className="text-[10px] text-white/55 truncate">{music.artist} · plays in background during your live</div>
            </div>
            <button type="button" onClick={() => setMusicOpen(true)} className="text-[11px] text-[#A855F7] font-bold px-2 py-1 rounded-full bg-white/5">Change</button>
            <button type="button" onClick={() => setMusic(null)} className="p-1 rounded-full hover:bg-white/10"><X className="w-3.5 h-3.5"/></button>
          </div>
        ) : (
          <button type="button" onClick={() => setMusicOpen(true)} data-testid="golive-music-open"
            className="w-full p-3 rounded-2xl bg-white/5 border border-dashed border-white/15 flex items-center gap-2 active:scale-[0.99]">
            <Music2 className="w-4 h-4 text-[#A855F7]"/>
            <span className="flex-1 text-left text-sm text-white/70">Add background music (optional)</span>
            <span className="text-[10px] text-[#A855F7] font-bold">Open →</span>
          </button>
        )}

        {err && <div className="text-[#FF2A5F] text-sm">{err}</div>}
        <button
          type="submit"
          data-testid="golive-submit"
          disabled={starting}
          aria-busy={starting}
          onPointerDown={() => { try { document.activeElement?.blur?.(); } catch { /* noop */ } }}
          className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF2A5F] to-[#00E5FF] text-black font-outfit font-bold active:scale-95 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
          {starting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin"/>Starting…
            </>
          ) : (
            <>
              <Camera className="w-5 h-5"/>Go Live Now
            </>
          )}
        </button>
      </form>

      <MusicPicker open={musicOpen} onClose={() => setMusicOpen(false)} onSelect={setMusic} selected={music?.id}/>
    </div>
  );
}

function Watch({ stream: initial }) {
  const nav = useNavigate();
  const [stream, setStream] = useState(initial);
  const [gifts, setGifts] = useState([]);
  const [showGift, setShowGift] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [usingLiveKit, setUsingLiveKit] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false); // try unmuted first — user just tapped a link, gesture is fresh
  const [audioReady, setAudioReady] = useState(false); // set true once we've received an audio track
  const [videoReady, setVideoReady] = useState(false); // set true once a remote video track has been attached
  const [joinError, setJoinError] = useState(null); // { kind: 'ended'|'locked'|'network', message: string }
  const [waiting, setWaiting] = useState(false); // "Waiting for the streamer's video…" after connect+5s with no track
  const [retryTick, setRetryTick] = useState(0); // bump to re-run join setup
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const roomRef = useRef(null);

  // Force-unmute (used by the tiny header icon as a fallback if the browser
  // silently blocks autoplay-with-sound despite the incoming link tap).
  const unmuteStream = async () => {
    try {
      if (remoteVideoRef.current) { remoteVideoRef.current.muted = false; try { await remoteVideoRef.current.play(); } catch { /* noop */ } }
      const a = remoteAudioRef.current;
      if (a) { a.muted = false; a.volume = 1.0; try { await a.play(); } catch { /* noop */ } }
      setAudioMuted(false);
    } catch { /* noop */ }
  };

  // Attempt to auto-unmute after a short delay. If the browser blocks it
  // (very rare when arriving via a link tap), we surface the tiny volume
  // toggle in the header — never a full-screen blocker.
  useEffect(() => {
    const t = setTimeout(async () => {
      const v = remoteVideoRef.current;
      const a = remoteAudioRef.current;
      try { if (a) { a.muted = false; a.volume = 1.0; await a.play(); } } catch { setAudioMuted(true); }
      try { if (v && !usingLiveKit) { v.muted = false; await v.play(); } } catch { setAudioMuted(true); }
    }, 250);
    return () => clearTimeout(t);
  }, [usingLiveKit]);

  useEffect(() => {
    let cancelled = false;
    let waitingTimer = null;
    setJoinError(null); setWaiting(false); setVideoReady(false);
    const setup = async () => {
      let data;
      try {
        const r = await api.post(`/live/${stream.id}/join`);
        data = r.data || {};
      } catch (e) {
        if (cancelled) return;
        const st = e?.response?.status;
        if (st === 404) {
          setJoinError({ kind: "ended", message: "This live has ended" });
        } else if (st === 402) {
          setJoinError({ kind: "locked", message: e?.response?.data?.detail || "Unlock this live to continue" });
        } else {
          setJoinError({ kind: "network", message: "Could not connect, tap to retry" });
        }
        return;
      }
      // If the backend indicates a mocked stream (no LiveKit configured), we
      // still play the mocked_url ONLY when the stream itself is marked mocked
      // by the server. We NEVER auto-fall-back on error — that used to hide
      // a truly-ended stream behind a fake sample video.
      if (!data.livekit_token || !data.livekit_url) {
        // Server says mocked → play the sample. Otherwise error out.
        if (data.mocked && data.mocked_url) {
          if (!cancelled) setUsingLiveKit(false);
          return;
        }
        if (!cancelled) setJoinError({ kind: "network", message: "Could not connect, tap to retry" });
        return;
      }

      // Real LiveKit viewer path
      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      const attachTrack = async (track) => {
        if (cancelled) return;
        for (let i = 0; i < 20; i++) {
          const target = track.kind === Track.Kind.Video ? remoteVideoRef.current : remoteAudioRef.current;
          if (target) {
            try { track.attach(target); } catch { /* noop */ }
            if (track.kind === Track.Kind.Video) { setVideoReady(true); setWaiting(false); }
            if (track.kind === Track.Kind.Audio) setAudioReady(true);
            return;
          }
          await new Promise((res) => setTimeout(res, 50));
        }
      };

      room.on(RoomEvent.TrackSubscribed, (track) => { attachTrack(track); });
      room.on(RoomEvent.TrackUnsubscribed, (track) => { try { track.detach(); } catch { /* noop */ } });

      if (!cancelled) setUsingLiveKit(true);
      try {
        await room.connect(data.livekit_url, data.livekit_token);
      } catch (e) {
        if (!cancelled) setJoinError({ kind: "network", message: "Could not connect, tap to retry" });
        return;
      }
      if (cancelled) return;

      // Drain any pre-published tracks.
      for (const participant of room.remoteParticipants.values()) {
        for (const pub of participant.trackPublications.values()) {
          if (pub.track) attachTrack(pub.track);
          else if (!pub.isSubscribed) { try { pub.setSubscribed(true); } catch { /* noop */ } }
        }
      }

      // If no video track has arrived within 5s, surface the waiting message.
      waitingTimer = setTimeout(() => { if (!cancelled) setWaiting((w) => (videoReady ? false : true)); }, 5000);
    };
    setup();
    return () => {
      cancelled = true;
      if (waitingTimer) clearTimeout(waitingTimer);
      if (roomRef.current) {
        try { roomRef.current.disconnect(); } catch { /* noop */ }
        roomRef.current = null;
      }
      api.post(`/live/${stream.id}/leave`).catch(()=>{});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.id, retryTick]);

  // If a video track lands, clear the waiting flag.
  useEffect(() => { if (videoReady) setWaiting(false); }, [videoReady]);

  // ── PK Battle polling (viewer side) ─────────────────────────────────
  // Every 4s check /battles/active for a battle whose host_stream_id or
  // opponent_stream_id matches this viewer's current stream. When found,
  // render the split-screen view on top of the primary video — WITHOUT
  // disconnecting the primary LiveKit room, so the host's video never
  // goes black during battle start/end transitions.
  const [chatTick, setChatTick] = useState(0);
  const [battle, setBattle] = useState(null);
  const [battleMySide, setBattleMySide] = useState(null); // "host" | "opponent"
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get("/battles/active");
        if (!alive) return;
        const list = r.data || [];
        const match = list.find(b => b.status === "active" && (b.host_stream_id === stream.id || b.opponent_stream_id === stream.id));
        if (match) {
          setBattle(match);
          setBattleMySide(match.host_stream_id === stream.id ? "host" : "opponent");
        } else {
          // `battle` in this closure is always the initial null, so the old
          // `else if (battle)` never fired and viewers stayed in split view
          // after the battle ended. Clearing unconditionally is a no-op when
          // already null.
          setBattle(null);
          setBattleMySide(null);
        }
      } catch { /* noop */ }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.id]);

  useEffect(() => {
    const t = setInterval(() => {
      api.get(`/live/${stream.id}`).then(r => setStream(r.data)).catch(()=>{});
      api.get(`/live/${stream.id}/gifts`).then(r => setGifts(r.data));
    }, 4000);
    return () => clearInterval(t);
  }, [stream.id]);

  return (
    <div className="fixed inset-0 z-50 bg-black max-w-md mx-auto">
      {/* LiveKit remote video & audio — ALWAYS mounted so refs are ready the
          instant tracks arrive. Fixes viewer black-screen race. Hidden with
          `display:none` (not unmount) while falling back to mocked playback.
          During an active PK battle the video is shrunk to the LEFT half via
          CSS — the primary LiveKit connection is NEVER disconnected, so the
          host's video keeps playing across battle start/end transitions. */}
      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        muted={audioMuted}
        className={battle
          ? "absolute battle-half left-0 w-1/2 object-cover rounded-none border-r border-white/10"
          : "absolute inset-0 w-full h-full object-cover"}
        style={{ display: usingLiveKit ? "block" : "none" }}
        data-testid="watch-livekit-video"
      />
      <audio ref={remoteAudioRef} autoPlay muted={audioMuted}/>
      {/* Mocked-playback fallback is used ONLY when the server explicitly
          returned a mocked stream — never when a real /join call fails. */}
      {!usingLiveKit && !joinError && stream?.mocked && stream?.mocked_url && (
        <video src={stream.mocked_url} autoPlay loop muted={audioMuted} playsInline
          className={battle
            ? "absolute battle-half left-0 w-1/2 object-cover rounded-none border-r border-white/10"
            : "absolute inset-0 w-full h-full object-cover"}/>
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 pointer-events-none"/>

      {/* PK Battle split-screen — mounts only during active battle. Opens a
          second LiveKit connection to the opponent's stream and renders the
          score HUD. Primary video connection is untouched. */}
      <BattleSplitView battle={battle} mySide={battleMySide} viewer/>

      {/* Rising-gift animation — ALL viewers see each incoming gift
          float from bottom to mid-screen, growing in size. During battle,
          gifts animate centered inside the recipient's window. */}
      <LiveGiftFlow gifts={gifts} battle={battle} mySide={battleMySide}/>

      {/* Join-error overlays — precise, per-status messaging (support-mandated). */}
      {joinError && (
        <div className="absolute inset-0 z-40 flex flex-col items-center justify-center px-6 text-center" data-testid={`watch-error-${joinError.kind}`}>
          <div className="mb-3 text-5xl">
            {joinError.kind === "ended" ? "📴" : joinError.kind === "locked" ? "🔒" : "⚠️"}
          </div>
          <p className="text-white text-base font-bold mb-2">{joinError.message}</p>
          {joinError.kind === "ended" && (
            <button onClick={() => nav(-1)} data-testid="watch-back-ended" className="mt-3 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 text-sm font-bold active:scale-95">Back</button>
          )}
          {joinError.kind === "locked" && (
            <button onClick={() => nav(`/premium/${stream?.id || ""}`)} data-testid="watch-unlock" className="mt-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF2A5F] to-[#FF7A00] text-white text-sm font-bold active:scale-95">Unlock</button>
          )}
          {joinError.kind === "network" && (
            <button onClick={() => { setJoinError(null); setRetryTick(t => t + 1); }} data-testid="watch-retry" className="mt-3 px-5 py-2.5 rounded-full bg-[#00E5FF] text-black text-sm font-bold active:scale-95">Tap to retry</button>
          )}
        </div>
      )}

      {/* Waiting overlay — connected but no video track yet (support-mandated). */}
      {usingLiveKit && waiting && !videoReady && !joinError && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center pointer-events-none" data-testid="watch-waiting">
          <Loader2 className="w-8 h-8 animate-spin text-white/70 mb-2"/>
          <p className="text-white/85 text-sm font-bold">Waiting for the streamer's video…</p>
        </div>
      )}

      {/* Auto-unmute is attempted on mount. In the rare case a browser blocks
          autoplay-with-sound, a tiny speaker icon appears next to the LIVE badge —
          NEVER a full-screen tap catcher (which used to block gift/close buttons). */}

      <div className="absolute top-4 inset-x-4 flex items-center justify-between z-10">
        <button onClick={()=>nav(-1)} data-testid="watch-back" className="w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center"><ArrowLeft className="w-5 h-5"/></button>
        <div className="flex items-center gap-2">
          {/* Top 3 gifters widget — tap to open full list (Today / 7d / Overall) */}
          <TopGiftersWidget hostUserId={stream.user?.id || stream.user_id}/>
          {audioMuted && (
            <button
              onClick={unmuteStream}
              data-testid="watch-unmute"
              aria-label="Unmute stream"
              className="w-9 h-9 rounded-full bg-[#FF2A5F] flex items-center justify-center animate-pulse active:scale-90">
              <Volume2 className="w-5 h-5"/>
            </button>
          )}
          <span className="px-2.5 py-1 rounded-full bg-[#FF2A5F] text-xs font-extrabold flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/>LIVE
          </span>
          {/* Host @username beside the LIVE badge — stays tappable during a PK
              battle, where live-host-card is hidden (support #274328). */}
          {stream.user?.id && (
            <Link to={`/u/${stream.user.id}`} data-testid="live-host-id-top"
              className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-xs font-bold max-w-[40vw] truncate active:scale-95">
              @{stream.user?.username}
            </Link>
          )}
          <LiveViewersButton streamId={stream.id} fallback={stream.viewers}/>
        </div>
      </div>

      {/* Host card — tap to open the streamer's profile (support #273638). In a
          battle each window's @username header is the tap target instead. */}
      <Link to={stream.user?.id ? `/u/${stream.user.id}` : "#"} data-testid="live-host-card"
        className={`absolute top-16 left-4 right-4 z-10 flex items-center gap-2 glass rounded-2xl p-2.5 active:scale-[0.98] ${battle ? "hidden" : ""}`}>
        <img src={stream.user?.avatar} className="w-10 h-10 rounded-full border-2 border-[#FF2A5F]"/>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm truncate">@{stream.user?.username}</div>
          <div className="flex items-center gap-1 text-[10px]">
            <span className="px-1.5 py-0.5 rounded bg-[#FF7A00]/20 text-[#FF7A00] font-bold">{stream.user?.stream_badge || "rookie"}</span>
            <span className="text-white/60">Lv {stream.user?.stream_level || 1}</span>
          </div>
        </div>
        <span className="shrink-0 px-2.5 py-1 rounded-full bg-[#FF2A5F] text-[11px] font-bold">Profile</span>
      </Link>

      {/* Persistent monitoring notice (viewer) — visible from start to end */}
      <div data-testid="live-monitor-banner-watcher" className={`absolute ${battle ? "top-14" : "top-32"} inset-x-4 z-[35] rounded-xl bg-black/55 backdrop-blur-md border border-[#FF7A00]/40 px-3 py-1.5 text-[10px] leading-tight text-white text-center`}>
        <span className="text-[#FF7A00] font-bold">Welcome to Hifireels.</span> This chat/live is <b>monitored</b> — be respectful and follow our guidelines.
      </div>

      {/* Viewer-side comment feed */}
      <LiveChatFeed streamId={stream.id} refreshKey={chatTick} className="absolute left-3 right-24 bottom-16 z-20 max-h-28"/>

      {/* Gift feed overlay */}
      <div className="absolute bottom-48 left-3 right-20 z-10 space-y-1.5 max-h-32 overflow-hidden">
        <AnimatePresence>
          {gifts.slice(0, 5).map(g => (
            <motion.div key={g.id} initial={{x:-50, opacity:0}} animate={{x:0, opacity:1}} exit={{opacity:0}}
              className="glass rounded-full px-3 py-1.5 flex items-center gap-2 max-w-fit">
              <span className="text-xl">{g.gift_emoji}</span>
              <span className="text-xs"><b>@{g.from_username}</b> sent <b className="text-[#FF7A00]">{g.gift_name}</b></span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Viewer bottom bar — comment input + send + like (hearts) + gift. Share button REMOVED per support fix #6. */}
      <ViewerBottomBar
        streamId={stream.id}
        onOpenGift={() => setShowGift(true)}
        onSent={() => setChatTick(t => t + 1)}
      />

      {showGift && <LiveGiftSheet streamId={stream.id} onClose={()=>setShowGift(false)} />}
    </div>
  );
}

/**
 * HostChatBar — the streamer's own comment box on the inner (host) screen so
 * they can reply to viewers in text (support #273638). Same endpoint viewers use.
 */
function HostChatBar({ streamId, onSent }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t) return;
    setText("");
    try { await api.post(`/live/${streamId}/chat`, { text: t }); setErr(""); onSent?.(); }
    catch (er) { setText(t); setErr(er?.response?.data?.detail || "Message failed to send"); setTimeout(() => setErr(""), 3000); }
  };
  return (
    <form onSubmit={send} className="flex items-center gap-2" data-testid="host-chat-bar">
      <input value={text} onChange={(e) => setText(e.target.value)} maxLength={280} placeholder={err || "Reply to your viewers..."}
        className={`flex-1 min-w-0 h-10 px-4 rounded-full bg-white/10 border ${err ? "border-[#FF2A5F]" : "border-white/15"} text-sm outline-none placeholder:text-white/50`}
        data-testid="host-chat-input"/>
      <button type="submit" disabled={!text.trim()} className="w-10 h-10 shrink-0 rounded-full bg-[#00E5FF] text-black flex items-center justify-center disabled:opacity-50 active:scale-90" data-testid="host-chat-send" aria-label="Send">
        <Send className="w-4 h-4"/>
      </button>
    </form>
  );
}

/**
 * ViewerBottomBar — bottom controls on the viewer screen.
 * - Comment text input + send button (posts to /live/{id}/comment if available; falls back to local echo)
 * - Like button that spawns rising hearts
 * - Gift button opens the LiveGiftSheet
 * (Share button removed per support fix #6.)
 */
function ViewerBottomBar({ streamId, onOpenGift, onSent }) {
  const [sendErr, setSendErr] = useState("");
  const [text, setText] = useState("");
  const [hearts, setHearts] = useState([]);
  const retire = (id) => setHearts(h => h.filter(x => x.id !== id));
  const sendComment = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t) return;
    setText("");
    try { await api.post(`/live/${streamId}/chat`, { text: t }); setSendErr(""); onSent?.(); }
    catch (err) { setSendErr(err?.response?.data?.detail || "Comment failed to send"); setTimeout(() => setSendErr(""), 3000); }
  };
  const likePulse = () => {
    const stamp = Date.now();
    setHearts(h => [...h, { id: `${stamp}-${Math.random()}`, left: 40 + Math.random() * 40 }]);
    try { api.post(`/live/${streamId}/like`).catch(() => {}); } catch { /* noop */ }
  };
  return (
    <>
      {/* Rising hearts — triggered by the like button */}
      <div className="pointer-events-none absolute inset-0 z-[22] overflow-hidden">
        <AnimatePresence>
          {hearts.map(h => (
            <motion.div
              key={h.id}
              initial={{ bottom: 72, opacity: 0, scale: 0.7 }}
              animate={{ bottom: ["12%", "60%"], opacity: [0, 1, 0], scale: [0.7, 1.3, 1] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.0, ease: "easeOut" }}
              onAnimationComplete={() => retire(h.id)}
              style={{ left: `${h.left}%` }}
              className="absolute text-3xl drop-shadow-[0_0_12px_rgba(255,42,95,0.9)]"
            >❤️</motion.div>
          ))}
        </AnimatePresence>
      </div>

      {sendErr && <div className="absolute bottom-16 left-3 right-3 z-20 text-center text-xs text-[#FF2A5F] font-bold">{sendErr}</div>}
      <form onSubmit={sendComment} className="absolute bottom-5 left-3 right-3 z-10 flex items-center gap-2" data-testid="watch-bottom-bar">
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Say something…"
          data-testid="watch-comment-input"
          className="flex-1 px-4 py-2.5 rounded-full bg-white/10 backdrop-blur text-sm text-white placeholder-white/50 outline-none border border-white/15 focus:border-[#00E5FF]"
        />
        <button type="submit" data-testid="watch-comment-send" aria-label="Send comment"
          className="w-10 h-10 rounded-full bg-[#00E5FF] text-black flex items-center justify-center active:scale-90">
          <Send className="w-5 h-5"/>
        </button>
        <button type="button" onClick={likePulse} data-testid="watch-like" aria-label="Like"
          className="w-10 h-10 rounded-full bg-[#FF2A5F] text-white flex items-center justify-center active:scale-90">
          <span className="text-xl">❤️</span>
        </button>
        <button type="button" onClick={onOpenGift} data-testid="watch-gift" aria-label="Open gifts"
          className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF7A00] to-[#FF2A5F] flex items-center justify-center neon-gold active:scale-90">
          <Gift className="w-5 h-5 text-black"/>
        </button>
      </form>
    </>
  );
}

// Custom gift sheet for live streams (uses /live/gift endpoint).
// Supports COMBO: tapping the same gift within 2s accumulates a multiplier
// (×2, ×3, …) and fires one request per tap for instant feedback.
function LiveGiftSheet({ streamId, onClose }) {
  const { user, refresh } = useAuth();
  const [gifts, setGifts] = useState([]);
  const [msg, setMsg] = useState("");
  const [combo, setCombo] = useState({ id: null, count: 0 }); // currently-combo'd gift
  const comboTimer = useRef(null);

  useEffect(() => { api.get("/gifts").then(r => setGifts(r.data)); }, []);
  useEffect(() => () => { if (comboTimer.current) clearTimeout(comboTimer.current); }, []);

  const sendGift = (g) => {
    // Instant-tap: fire the request without awaiting so the UI responds
    // immediately. Multiple rapid taps accumulate a combo badge.
    api.post("/live/gift", { stream_id: streamId, gift_id: g.id })
      .then(() => refresh())
      .catch((e) => { setMsg(e?.response?.data?.detail || "Failed"); setTimeout(() => setMsg(""), 1600); });
    onClose?.();
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[60] max-w-md mx-auto" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
        <div className="absolute inset-0 bg-black/70" onClick={onClose} data-testid="livegift-backdrop"/>
        <motion.div initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring", damping:30}}
          className="absolute bottom-0 inset-x-0 glass rounded-t-3xl max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h3 className="font-outfit font-bold text-lg">Send Live Gift</h3>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-[#FF7A00] font-bold text-sm"><span className="font-outfit">★</span> {user?.wallet_goldly}</span>
              <button onClick={onClose} data-testid="livegift-close" aria-label="Close gift picker" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center active:scale-90"><X className="w-5 h-5"/></button>
            </div>
          </div>
          {msg && <div className="px-4 py-2 text-center text-sm text-[#00E5FF]">{msg}</div>}
          <div className="overflow-y-auto p-3 grid grid-cols-4 gap-2 scrollbar-hide">
            {gifts.map(g => {
              const isCombo = combo.id === g.id && combo.count > 1;
              return (
                <button key={g.id} onClick={() => sendGift(g)} data-testid={`livegift-${g.id}`}
                  className={`relative flex flex-col items-center p-2 rounded-2xl border active:scale-95 transition-colors ${isCombo ? "bg-[#FF7A00]/25 border-[#FF7A00]" : "bg-white/5 border-white/10"}`}>
                  {isCombo && (
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full bg-[#FF2A5F] text-white text-[10px] font-extrabold leading-none shadow-lg animate-pulse" data-testid={`livegift-combo-${g.id}`}>×{combo.count}</span>
                  )}
                  <div className="text-3xl">{g.emoji}</div>
                  <div className="text-[10px] mt-1 truncate w-full text-center text-white/80">{g.name}</div>
                  <div className="text-[11px] text-[#FF7A00] font-bold mt-0.5"><span className="font-outfit">★</span>{g.price}</div>
                </button>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function Live() {
  const { streamId } = useParams();
  const [tab, setTab] = useState("watch");
  const [watching, setWatching] = useState(null);
  const [badges, setBadges] = useState([]);

  useEffect(() => { api.get("/live/badges").then(r => setBadges(r.data)); }, []);

  // ── Swipe between lives (support #274116) ───────────────────────────────
  // While watching, keep the list of live streams so the viewer can swipe
  // up = next live / swipe down = previous live without leaving the page.
  const [liveList, setLiveList] = useState([]);
  const isWatching = !!watching;
  useEffect(() => {
    if (!isWatching) return undefined;
    let alive = true;
    const load = () => api.get("/live/active").then(r => { if (alive) setLiveList(r.data || []); }).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [isWatching]);
  const swipeStart = useRef(null);
  const switchLive = useCallback((dir) => {
    if (!watching) return;
    const ids = liveList.map(x => x.id);
    if (ids.length < 2) return;
    const i = ids.indexOf(watching.id);
    const next = ids[((i < 0 ? (dir > 0 ? -1 : 0) : i) + dir + ids.length) % ids.length];
    if (!next || next === watching.id) return;
    api.get(`/live/${next}`).then(r => setWatching(r.data)).catch(() => {});
  }, [liveList, watching]);
  useEffect(() => {
    if (!isWatching) return undefined;
    const onKey = (e) => {
      if (/^(INPUT|TEXTAREA)$/.test(e.target?.tagName || "")) return;
      if (e.key === "ArrowDown") switchLive(1);
      if (e.key === "ArrowUp") switchLive(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isWatching, switchLive]);
  const onSwipeStart = (e) => {
    const tgt = e.target;
    if (tgt?.closest?.('input,textarea,button,a,[data-testid*="chat"],[data-testid*="gift"]')) { swipeStart.current = null; return; }
    const t = e.touches?.[0];
    swipeStart.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onSwipeEnd = (e) => {
    const st = swipeStart.current; swipeStart.current = null;
    const t = e.changedTouches?.[0];
    if (!st || !t) return;
    const dx = t.clientX - st.x, dy = t.clientY - st.y;
    if (Math.abs(dy) > 90 && Math.abs(dy) > Math.abs(dx) * 1.5) switchLive(dy < 0 ? 1 : -1);
  };

  // Direct URL /live/watch/:streamId — auto-open the fullscreen Watch player.
  useEffect(() => {
    if (streamId && !watching) {
      api.get(`/live/${streamId}`).then(r => setWatching(r.data)).catch(() => {});
    }
  }, [streamId, watching]);

  if (watching) return (
    <div onTouchStart={onSwipeStart} onTouchEnd={onSwipeEnd} data-testid="watch-swipe-area">
      <Watch key={watching.id} stream={watching}/>
    </div>
  );

  return (
    <div className="px-4 py-6">
      <div className="flex items-center gap-2 mb-1">
        <Radio className="w-7 h-7 text-[#FF2A5F]"/>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Live</h1>
        <span className="ml-2 px-2 py-0.5 rounded-full bg-[#FF2A5F]/20 border border-[#FF2A5F]/30 text-[#FF2A5F] text-[10px] font-bold uppercase">Beta</span>
      </div>
      <p className="text-white/60 text-sm mb-5">Stream live & climb levels • Earn badges</p>

      <div className="flex gap-2 mb-5">
        <button onClick={()=>setTab("watch")} data-testid="tab-watch" className={`flex-1 py-2.5 rounded-full text-sm font-bold border ${tab==="watch"?"border-[#FF2A5F] bg-[#FF2A5F]/10 text-[#FF2A5F]":"border-white/10 bg-white/5"}`}>
          <Radio className="inline w-4 h-4 mr-1"/>Watch Live
        </button>
        <button onClick={()=>setTab("golive")} data-testid="tab-golive" className={`flex-1 py-2.5 rounded-full text-sm font-bold border ${tab==="golive"?"border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]":"border-white/10 bg-white/5"}`}>
          <Camera className="inline w-4 h-4 mr-1"/>Go Live
        </button>
        <button onClick={()=>setTab("badges")} data-testid="tab-badges" className={`flex-1 py-2.5 rounded-full text-sm font-bold border ${tab==="badges"?"border-[#FF7A00] bg-[#FF7A00]/10 text-[#FF7A00]":"border-white/10 bg-white/5"}`}>
          <Trophy className="inline w-4 h-4 mr-1"/>Badges
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <Link to="/battles" data-testid="quick-battles" className="flex items-center justify-center gap-2 p-3 rounded-2xl bg-gradient-to-r from-[#FF7A00]/15 to-[#FF2A5F]/15 border border-[#FF7A00]/30">
          <Swords className="w-4 h-4 text-[#FF7A00]"/>
          <span className="text-xs font-bold">Battles & PK</span>
        </Link>
        <Link to="/creator-league" data-testid="quick-cl" className="flex items-center justify-center gap-2 p-3 rounded-2xl bg-gradient-to-r from-[#A855F7]/15 to-[#FF2A5F]/15 border border-[#A855F7]/30">
          <Trophy className="w-4 h-4 text-[#A855F7]"/>
          <span className="text-xs font-bold">Creator League</span>
        </Link>
      </div>

      {tab === "watch" && <ActiveList onWatch={setWatching}/>}
      {tab === "golive" && <GoLive/>}
      {tab === "badges" && (
        <div className="space-y-2.5" data-testid="badges-list">
          <p className="text-xs text-white/60 mb-2">Achievement-based badges. Streamers level up by streaming time + gifts received.</p>
          {badges.map(b => (
            <div key={b.id} className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3" data-testid={`badge-${b.id}`}>
              <div className="text-4xl">{b.emoji}</div>
              <div className="flex-1">
                <div className="font-outfit font-bold capitalize" style={{color: b.color}}>{b.name}</div>
                <div className="text-xs text-white/60">{b.min_streams === 0 ? "Default for new streamers" : `${b.min_streams}+ streams${b.min_viewers ? ` • ${b.min_viewers.toLocaleString()}+ viewers` : ""}`}</div>
              </div>
            </div>
          ))}
          <div className="mt-4 p-4 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/30">
            <h3 className="font-outfit font-bold text-sm mb-2 flex items-center gap-1"><Sparkles className="w-4 h-4 text-[#00E5FF]"/>Level Tiers</h3>
            <div className="text-xs space-y-1 text-white/80">
              <div>Lv 1–10 → <b className="text-white">Beginner</b></div>
              <div>Lv 11–25 → <b className="text-[#00E5FF]">Rising Star</b></div>
              <div>Lv 26–50 → <b className="text-[#FF7A00]">Pro Streamer</b></div>
              <div>Lv 51–100 → <b className="text-[#FF2A5F]">Elite</b></div>
              <div>Lv 100+ → <b className="text-gradient-gold">Hall of Fame</b></div>
            </div>
            <p className="text-[10px] text-white/50 mt-2">XP: 5/min streamed + bonus from gifts received</p>
          </div>
        </div>
      )}
    </div>
  );
}
